import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { api, formatApiError } from "../../lib/api";
import { ErrorState } from "../../components/common/States";
import FeedbackThread from "../../components/feedback/FeedbackThread";

export default function TrackCase() {
  const [params] = useSearchParams();
  const { hash } = useLocation();
  const hashToken = new URLSearchParams(hash.slice(1)).get("token");
  const [creds, setCreds] = useState({ code: params.get("code") || "", pin: "" });
  const [fb, setFb] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const secret = () => (creds.token ? { code: creds.code, token: creds.token } : { code: creds.code, pin: creds.pin });

  const fetchCase = async (body) => {
    setLoading(true); setError(null);
    try { const { data } = await api.post("/public/track", body); setFb(data); }
    catch (err) { setFb(null); setError(formatApiError(err)); } finally { setLoading(false); }
  };
  useEffect(() => {
    if (hashToken && params.get("code")) {
      setCreds({ code: params.get("code"), pin: "", token: hashToken });
      fetchCase({ code: params.get("code"), token: hashToken });
    }
  }, [hashToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const lookup = async (e) => {
    e.preventDefault();
    if (!creds.code.trim() || !/^\d{6}$/.test(creds.pin)) { setError("Saisissez la référence et le code secret à 6 chiffres"); return; }
    fetchCase({ code: creds.code, pin: creds.pin });
  };
  const reply = async (message) => { const { data } = await api.post("/public/track/reply", { ...secret(), message }); setFb(data); };

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <p className="eyebrow">Suivi sécurisé</p>
      <h1 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">Suivre mon dossier</h1>
      <p className="mt-2 text-sm text-slate-500">Saisissez le numéro de référence et le code secret reçus lors de votre dépôt. Connecté ? Retrouvez vos dossiers dans votre espace.</p>
      {!fb && loading && hashToken && <p className="mt-8 text-sm text-slate-500" data-testid="track-token-loading">Ouverture sécurisée de votre dossier…</p>}
      {!fb && !(loading && hashToken) && (
        <form onSubmit={lookup} className="card-surface mt-8 grid gap-4 p-5 sm:grid-cols-3" data-testid="track-form" noValidate>
          <div className="sm:col-span-2"><Label>Numéro de référence</Label><Input value={creds.code} onChange={(e) => setCreds({ ...creds, code: e.target.value })} placeholder="FB-XXXXXXXX" className="mt-1.5 h-11 font-mono uppercase" data-testid="track-code-input" /></div>
          <div><Label>Code secret</Label><Input value={creds.pin} inputMode="numeric" maxLength={6} onChange={(e) => setCreds({ ...creds, pin: e.target.value.replace(/\D/g, "") })} placeholder="••••••" className="mt-1.5 h-11 font-mono tracking-[0.3em]" data-testid="track-pin-input" /></div>
          <Button type="submit" className="h-11 rounded-full sm:col-span-3" disabled={loading} data-testid="track-submit-button">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Consulter mon dossier</Button>
        </form>
      )}
      {error && <div className="mt-6"><ErrorState message={error} /></div>}
      {fb && (
        <div className="mt-8 space-y-4">
          <FeedbackThread fb={fb} onReply={reply} />
          <Button variant="ghost" onClick={() => { setFb(null); setCreds({ code: "", pin: "" }); window.history.replaceState(null, "", "/suivi"); }} data-testid="track-close-button">Consulter un autre dossier</Button>
        </div>
      )}
    </div>
  );
}
